"use client"

import * as React from "react"
import { useId } from "react"
import { Calendar } from "@ui/shad"
import { addWeeks, format, startOfDay } from "date-fns"

import { SimpleFormDialog } from "@/components/dialogs"

import { TimeSelect } from "../time-picker/time-select"
import {
  availableScheduleTimes,
  firstAvailableScheduleSelection,
  recipientSummaryLabel,
  recipientTimeLabel,
  scheduleCandidate,
  validScheduleTimezone,
} from "./schedule-timezone"
import { MAX_SCHEDULE_WEEKS, SCHEDULE_END_HOUR, SCHEDULE_START_HOUR, type ScheduleModalProps } from "./types"

const TIME_OPTION_INTERVAL_MINUTES = 60

export function ScheduleModal({
  open,
  onOpenChange,
  onConfirm,
  disabled = false,
  recipientTimezone,
}: ScheduleModalProps) {
  const dialogFormId = useId()

  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>()
  const [selectedTime, setSelectedTime] = React.useState<string>("")
  const normalizedRecipientTimezone = React.useMemo(() => validScheduleTimezone(recipientTimezone), [recipientTimezone])
  const advisorTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const showRecipientTime = normalizedRecipientTimezone !== null && normalizedRecipientTimezone !== advisorTimezone

  React.useEffect(() => {
    if (!open) return

    const selection = firstAvailableScheduleSelection(
      new Date(),
      normalizedRecipientTimezone,
      SCHEDULE_START_HOUR,
      SCHEDULE_END_HOUR,
      MAX_SCHEDULE_WEEKS * 7
    )
    setSelectedDate(selection?.date)
    setSelectedTime(selection?.time ?? "")
  }, [open, normalizedRecipientTimezone])

  const selectableTimes = React.useMemo(
    () =>
      selectedDate
        ? availableScheduleTimes(
            selectedDate,
            new Date(),
            normalizedRecipientTimezone,
            SCHEDULE_START_HOUR,
            SCHEDULE_END_HOUR
          )
        : [],
    [selectedDate, normalizedRecipientTimezone]
  )
  const selectableTimeSet = React.useMemo(() => new Set(selectableTimes), [selectableTimes])

  React.useEffect(() => {
    if (selectableTimeSet.has(selectedTime)) return

    setSelectedTime(selectableTimes[0] ?? "")
  }, [selectableTimeSet, selectableTimes, selectedTime])

  const handleConfirm = () => {
    if (!selectedDate || !selectableTimeSet.has(selectedTime)) return

    const [hours, minutes] = selectedTime.split(":").map(Number)
    const scheduledFor = scheduleCandidate(selectedDate, hours, minutes)
    onConfirm(scheduledFor.toISOString())
  }

  const today = startOfDay(new Date())
  const maxDate = addWeeks(today, MAX_SCHEDULE_WEEKS)
  const minDate =
    firstAvailableScheduleSelection(
      new Date(),
      normalizedRecipientTimezone,
      SCHEDULE_START_HOUR,
      SCHEDULE_END_HOUR,
      MAX_SCHEDULE_WEEKS * 7
    )?.date ?? today
  const selectedDateTime =
    selectedDate && selectedTime
      ? scheduleCandidate(selectedDate, Number(selectedTime.split(":")[0]), Number(selectedTime.split(":")[1]))
      : null
  const isValid = selectedDate && selectableTimeSet.has(selectedTime)

  const optionLabel = React.useCallback(
    (hour: number, minute: number) => {
      if (!selectedDate) return ""

      const candidate = scheduleCandidate(selectedDate, hour, minute)
      const advisorTime = format(candidate, "h:mm a")
      if (!showRecipientTime || !normalizedRecipientTimezone) return advisorTime

      return `${advisorTime} (${recipientTimeLabel(candidate, normalizedRecipientTimezone)})`
    },
    [normalizedRecipientTimezone, selectedDate, showRecipientTime]
  )

  return (
    <SimpleFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Schedule send"
      action={{ label: "Schedule send", form: dialogFormId, disabled: !isValid || disabled }}
    >
      <form
        id={dialogFormId}
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (!isValid || disabled) return
          void handleConfirm()
        }}
      >
        <div className="space-y-2">
          <Calendar
            mode="single"
            disableInput
            selected={selectedDate}
            onSelect={setSelectedDate}
            disabled={{ before: minDate, after: maxDate }}
            startMonth={minDate}
            endMonth={maxDate}
            initialFocus
            className="w-full"
            classNames={{
              months: "w-full",
              month: "w-full space-y-4",
              month_grid: "w-full border-collapse",
              weekdays: "flex w-full",
              weekday: "text-muted-foreground flex-1 text-center font-normal text-sm",
              week: "flex w-full mt-2",
              day: "flex-1 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent [&.outside:has([aria-selected])]:bg-accent/50 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
              day_button:
                "day-button h-9 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md",
              selected:
                "[&>.day-button]:bg-primary [&>.day-button]:text-primary-foreground [&>.day-button:hover]:bg-primary [&>.day-button:hover]:text-primary-foreground [&>.day-button:focus]:bg-primary [&>.day-button:focus]:text-primary-foreground",
            }}
          />

          <div className="space-y-2">
            <label className="text-sm font-medium">Time</label>
            <TimeSelect
              value={selectedTime}
              onChange={setSelectedTime}
              startHour={normalizedRecipientTimezone ? 0 : SCHEDULE_START_HOUR}
              endHour={normalizedRecipientTimezone ? 23 : SCHEDULE_END_HOUR}
              intervalMinutes={TIME_OPTION_INTERVAL_MINUTES}
              includeNone={false}
              optionFilter={(hour, minute) =>
                selectableTimeSet.has(`${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`)
              }
              optionLabel={optionLabel}
              className="w-full"
            />
          </div>

          {selectedDateTime && (
            <div className="bg-muted rounded-md p-3 text-sm">
              <span className="text-muted-foreground">Will send on </span>
              <span className="font-medium">{format(selectedDateTime, "EEE, MMMM d 'at' h:mm a")}</span>
              {showRecipientTime && normalizedRecipientTimezone && (
                <div className="text-muted-foreground mt-1">
                  Client local time: {recipientSummaryLabel(selectedDateTime, normalizedRecipientTimezone)}
                </div>
              )}
            </div>
          )}
        </div>
      </form>
    </SimpleFormDialog>
  )
}
